import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, ChevronRight, Trash2 } from "lucide-react";
import { Modal, Tooltip, message } from "antd";
import { deleteClient } from "../../Utils/client/client";

const ROLES = [
  'superadmin',
  'client',
  'domain_manager',
  'exploitation_manager'
];

const ROLE_COLORS = {
  superadmin: {
    border: 'border-rose-500',
    hover: 'group-hover:bg-rose-50',
    button: 'bg-rose-500 hover:bg-rose-600',
    logo: 'bg-rose-400 group-hover:bg-rose-500',
    text: 'group-hover:text-rose-500',
    icon: 'text-rose-500'
  },
  client: {
    border: 'border-sky-500',
    hover: 'group-hover:bg-sky-50',
    button: 'bg-sky-500 hover:bg-sky-600',
    logo: 'bg-sky-400 group-hover:bg-sky-500',
    text: 'group-hover:text-sky-500',
    icon: 'text-sky-500'
  },
  domain_manager: {
    border: 'border-amber-500',
    hover: 'group-hover:bg-amber-50',
    button: 'bg-amber-500 hover:bg-amber-600',
    logo: 'bg-amber-400 group-hover:bg-amber-500',
    text: 'group-hover:text-amber-500',
    icon: 'text-amber-500'
  },
  exploitation_manager: {
    border: 'border-emerald-500',
    hover: 'group-hover:bg-emerald-50',
    button: 'bg-emerald-500 hover:bg-emerald-600',
    logo: 'bg-emerald-400 group-hover:bg-emerald-500',
    text: 'group-hover:text-emerald-500',
    icon: 'text-emerald-500'
  }
};

const ROLE_LABELS = {
  'superadmin': 'Administrateur',
  'client': 'Client',
  'domain_manager': 'Gérant de domaine',
  'exploitation_manager': 'Gérant d\'exploitation'
};

const ClientCard = ({ clientInfo, onDelete, role = 'client' }) => {
  const user = clientInfo.user;
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const colors = ROLE_COLORS[role] || ROLE_COLORS.client;

  const handleDelete = async () => {
    try {
      await deleteClient(user.user_id);
      onDelete?.(user.user_id);
      setShowDeleteModal(false);
      messageApi.success('Client supprimé avec succès');
    } catch (error) {
      console.error("Failed to delete client:", error);
      messageApi.error('Erreur lors de la suppression du client');
    }
  };

  const handleCardClick = () => {
    navigate(`/admin/client/${user?.user_id}/show-info`);
  };

  return (
    <>
      {contextHolder}
      <div className="relative w-80">
        <div
          className="bg-white shadow-lg rounded-2xl overflow-hidden group hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
          onClick={handleCardClick}
        >
          <div className="absolute top-4 right-4 z-20">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteModal(true);
              }}
              className="p-2 rounded-full bg-red-100 hover:bg-red-200 transition-colors duration-300 group/delete"
            >
              <Trash2 className="w-5 h-5 text-red-600 group-hover/delete:text-red-700" />
            </button>
          </div>

          <div className={`p-6 ${colors.border} border-4 rounded-2xl border-b-0 rounded-b-none ${colors.hover}`}>
            <div className="flex items-center justify-center mb-4 group-hover:scale-105">
              {user.company_logo ? (
                <img
                  src={`data:image/png;base64,${user.company_logo}`}
                  alt={user?.company_name}
                  className={`h-24 w-24 rounded-full object-cover border-4 ${colors.border} group-hover:shadow-lg`}
                />
              ) : (
                <div className={`h-24 w-24 rounded-full ${colors.logo} flex items-center justify-center text-4xl font-bold text-white group-hover:shadow-lg`}>
                  {user?.company_name?.charAt(0)}
                </div>
              )}
            </div>
            <h2 className={`text-2xl font-bold text-center truncate ${colors.text}`}>
              {user?.company_name}
            </h2>
            <div className="mt-3 flex justify-center">
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold text-white ${colors.button}`}>
                {ROLE_LABELS[role] || role}
              </span>
            </div>
          </div>

          <div className={`p-6 ${colors.border} rounded-2xl rounded-t-none border-t-0 border-4 transition-colors duration-300`}>
            <div className="flex items-center mb-4">
              <User className={`mr-2 ${colors.icon}`} />
              <div className="min-w-0 flex-1">
                <span className="text-gray-700 block truncate">
                  Nom complet gérant : <span className="font-semibold">{user?.first_name} {user?.last_name}</span>
                </span>
              </div>
            </div>
            <div className="flex items-center mb-6">
              <span className={`mr-2 ${colors.icon} text-xl font-bold`}>@</span>
              <div className="min-w-0 flex-1">
                <span className="text-gray-700 block truncate">
                  Email : <span className="font-semibold">{user?.email}</span>
                </span>
              </div>
            </div>
            <Link
              to={`admin/client/${user?.user_id}/show-info`}
              state={{ clientInfo }}
              className={`group/button block w-full text-center py-2 px-4 ${colors.button} text-white font-semibold rounded-lg transition-all duration-300 hover:shadow-md relative overflow-hidden`}
            >
              <span className="relative z-10 inline-block transition-transform duration-300 group-hover/button:translate-x-[-0.5rem]">
                Plus de détails
              </span>
              <ChevronRight className="absolute right-4 top-1/2 transform -translate-y-1/2 opacity-0 transition-all duration-300 group-hover/button:opacity-100 group-hover/button:translate-x-0" />
            </Link>
          </div>
        </div>
      </div>

      <Modal
        title="Confirmer la suppression"
        open={showDeleteModal}
        onOk={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
        okText="Supprimer"
        cancelText="Annuler"
        okButtonProps={{
          danger: true,
          className: "bg-red-600 hover:bg-red-700"
        }}
      >
    <p>Êtes-vous sûr de vouloir supprimer l'utilisateur {user?.company_name} ? Cette action est irréversible.</p>
  </Modal>
    </>
  );
};

export default ClientCard;